import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { HiCheck } from 'react-icons/hi';
import Button from "../../../components/button/button";
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import { upgradeSubscription, cancelUpComingSubscription, getPlans } from '../../../services/billing';
import { formatCents, planFeatures } from "../plan-features";
import { SUBSCRIPTION_BASE, Pill, Notice, LoadingState } from "../components";

const billing_plan_to_proceed_key = 'billing_plan_to_proceed_key';

function PlanCard({ plan, currentPlan, busy, onChangePlan, onCancelUpComingSubscription, onViewUsage }) {
    const isUpgrade = !currentPlan || plan.priceMonthlyCents > currentPlan.priceMonthlyCents;

    let action;
    if (plan.currentPlan) {
        action = <Button rootClass="w-full [&>div]:w-full [&_button]:w-full" text="View usage" color="default" onClick={onViewUsage} />;
    } else if (plan.upcomingPlan) {
        action = <Button rootClass="w-full [&>div]:w-full [&_button]:w-full" text="Cancel scheduled change" color="default"
                         onClick={onCancelUpComingSubscription} disabled={busy} />;
    } else {
        action = <Button rootClass="w-full [&>div]:w-full [&_button]:w-full" text={isUpgrade ? `Upgrade to ${plan.displayName}` : `Downgrade to ${plan.displayName}`}
                         color={isUpgrade ? 'primary' : 'default'} onClick={() => onChangePlan(plan, isUpgrade)} disabled={busy} />;
    }

    return (
        <div className={`flex flex-col rounded-xl border bg-white p-5 transition-shadow ${
            plan.currentPlan
                ? 'border-primary ring-1 ring-primary'
                : 'border-gray-200 hover:shadow-md'
        }`}>
            <div className="flex min-h-[1.5rem] items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900">{plan.displayName}</h3>
                {plan.currentPlan && <Pill tone="accent" icon={false}>Current plan</Pill>}
                {plan.upcomingPlan && <Pill tone="info">Starts next period</Pill>}
            </div>

            <p className="mt-4 text-gray-900">
                <span className="text-4xl font-semibold tracking-tight">{formatCents(plan.priceMonthlyCents)}</span>
                <span className="ml-1 text-sm text-gray-500">/ month</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">
                {plan.includedConversations.toLocaleString('en-US')} conversations included
            </p>

            <div className="mt-5">{action}</div>

            <ul className="mt-6 space-y-3 border-t border-gray-100 pt-5">
                {planFeatures(plan).map((feature, index) =>
                    <li className="flex gap-2.5" key={index}>
                        <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-gray-900 dark:text-brand-lime" />
                        <span className="text-sm leading-snug text-gray-600">{feature}</span>
                    </li>
                )}
            </ul>
        </div>
    );
}

export default function Index() {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loadState, setLoadState] = useState({ loading: true, failed: false });
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null); // { tone, text }

    const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState(false);
    const [planChange, setPlanChange] = useState(null);

    async function fetchData() {
        const { response, status } = await getPlans();
        if (status === 200) {
            setPlans(response);
            setLoadState({ loading: false, failed: false });
        } else {
            setLoadState({ loading: false, failed: true });
        }
    }

    // resumes a plan change that was interrupted to add a payment method
    async function checkProceedingPlan() {
        const plan = sessionStorage.getItem(billing_plan_to_proceed_key);
        if (plan) {
            sessionStorage.removeItem(billing_plan_to_proceed_key);
            await onChangeSubscription(plan);
        }
    }

    useEffect(() => {
        fetchData();
        checkProceedingPlan();
    }, []);

    const onChangeSubscription = async plan => {
        setFeedback(null);
        setBusy(true);
        const { status, response } = await upgradeSubscription(plan);
        setBusy(false);
        if (status === 200) {
            setFeedback({ tone: 'success', text: `Your plan change to ${plan} is saved.` });
            await fetchData();
        } else if (status === 400 && response === 'payment_method_not_found') {
            sessionStorage.setItem(billing_plan_to_proceed_key, plan);
            navigate(`${SUBSCRIPTION_BASE}/payment-methods?setup=1`);
        } else if (status === 402) {
            setFeedback({ tone: 'danger', text: 'Payment failed. Please update your payment method and try again.' });
        } else {
            setFeedback({ tone: 'danger', text: 'Unable to update the plan. Please try again.' });
        }
    };

    const handleConfirmPlanChange = async confirmed => {
        const change = planChange;
        setPlanChange(null);
        if (confirmed) {
            await onChangeSubscription(change.plan.name);
        }
    };

    const handleConfirmCancelSubscription = async confirmed => {
        setOpenCancelConfirmDialog(false);
        if (confirmed) {
            setFeedback(null);
            setBusy(true);
            const { status } = await cancelUpComingSubscription();
            setBusy(false);
            setFeedback(status === 200
                ? { tone: 'success', text: 'The scheduled plan change is canceled. You keep your current plan.' }
                : { tone: 'danger', text: 'Unable to cancel the scheduled change. Please try again.' });
            await fetchData();
        }
    };

    if (loadState.loading) {
        return <LoadingState text="Loading plans..." />;
    }
    if (loadState.failed) {
        return <Notice tone="danger" title="Unable to load plans"
                       action={<Button text="Retry" color="default" onClick={() => { setLoadState({ loading: true }); fetchData(); }} />} />;
    }

    const currentPlan = plans.find(plan => plan.currentPlan);
    // Hide the $49 package while keeping current-plan data for upgrade/downgrade comparisons.
    const visiblePlans = plans.filter(plan => Number(plan.priceMonthlyCents) !== 4900);

    return (
        <div className="flex flex-col gap-6">
            {openCancelConfirmDialog &&
                <ConfirmDialog
                    open={true}
                    onCompletion={handleConfirmCancelSubscription}
                    message="Cancel the scheduled plan change and keep your current plan?"
                />
            }

            {planChange &&
                <ConfirmDialog
                    open={true}
                    onCompletion={handleConfirmPlanChange}
                    message={planChange.isUpgrade
                        ? `Upgrade to ${planChange.plan.displayName}? The new plan starts now and the difference is charged to your card today.`
                        : `Downgrade to ${planChange.plan.displayName}? You keep your current plan until the end of this billing period.`}
                />
            }

            {busy && <Notice tone="info">Updating your subscription...</Notice>}
            {feedback && !busy && <Notice tone={feedback.tone}>{feedback.text}</Notice>}

            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <h2 className="text-base font-semibold text-gray-900">Choose a plan</h2>
                    <p className="text-sm text-gray-500">
                        Upgrades start right away and are prorated. Downgrades take effect at the end of the billing period.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {visiblePlans.map(plan =>
                    <PlanCard key={plan.name}
                              plan={plan}
                              currentPlan={currentPlan}
                              busy={busy}
                              onChangePlan={(plan, isUpgrade) => setPlanChange({ plan, isUpgrade })}
                              onCancelUpComingSubscription={() => setOpenCancelConfirmDialog(true)}
                              onViewUsage={() => navigate(SUBSCRIPTION_BASE)}
                    />
                )}
            </div>
        </div>
    );
}
